import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { fmtEUR, fmtXOF } from "../lib/api";
import { sizeLabel } from "../lib/sizeLabel";

export const CartDrawer = () => {
  const { items, isOpen, closeCart, updateQuantity, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const goCheckout = () => {
    closeCart();
    navigate("/commande");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 z-[70] bg-noir/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed end-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-s border-ligne bg-card2"
            data-lenis-prevent
          >
            <div className="flex items-center justify-between border-b border-ligne px-6 py-5">
              <p className="font-serif text-xl text-ivoire">
                {t("cart.title")} <span className="text-or">({items.length})</span>
              </p>
              <button data-testid="cart-drawer-close-button" onClick={closeCart} className="text-argile transition-colors hover:text-or" aria-label={t("header.closeMenu")}>
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <div data-testid="cart-empty-state" className="flex h-full flex-col items-center justify-center gap-4 text-center">
                  <ShoppingBag size={36} strokeWidth={1} className="text-or/60" />
                  <p className="text-sm text-argile">{t("cart.empty")}</p>
                  <button
                    data-testid="cart-empty-browse-button"
                    onClick={() => { closeCart(); navigate("/boutique"); }}
                    className="mt-2 border border-or/60 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all hover:bg-or hover:text-noir"
                  >
                    {t("common.discoverShop")}
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((i) => (
                    <li key={i.key} data-testid={`cart-item-${i.slug}`} className="flex gap-4">
                      <div className="h-28 w-22 shrink-0 overflow-hidden border border-ligne" style={{ width: 88 }}>
                        <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-serif text-base text-ivoire">{i.name}</p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-argile">
                              {t("common.size")} {sizeLabel(i.size, t)}
                            </p>
                          </div>
                          <button data-testid={`cart-remove-${i.slug}`} onClick={() => removeItem(i.key)} className="text-argile transition-colors hover:text-or" aria-label={t("cart.remove", { name: i.name })}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center border border-ligne">
                            <button data-testid={`cart-qty-minus-${i.slug}`} onClick={() => updateQuantity(i.key, -1)} className="px-2.5 py-1.5 text-argile hover:text-or" aria-label={t("cart.decrease")}><Minus size={12} /></button>
                            <span data-testid={`cart-qty-${i.slug}`} className="w-8 text-center font-mono text-xs text-ivoire">{i.quantity}</span>
                            <button data-testid={`cart-qty-plus-${i.slug}`} onClick={() => updateQuantity(i.key, 1)} className="px-2.5 py-1.5 text-argile hover:text-or" aria-label={t("cart.increase")}><Plus size={12} /></button>
                          </div>
                          <p className="text-sm text-or">{fmtXOF(i.price_xof * i.quantity)}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-ligne px-6 py-6">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-argile">{t("common.subtotal")}</p>
                  <div className="text-end" data-testid="cart-subtotal">
                    <p className="text-lg text-ivoire">{fmtXOF(subtotal)}</p>
                    <p className="text-xs text-argile">{fmtEUR(subtotal)}</p>
                  </div>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={goCheckout}
                  className="mt-5 w-full bg-or py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all duration-300 hover:bg-orclair"
                >
                  {t("cart.checkout")}
                </button>
                <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-argile/70">
                  {t("cart.momoNote")}
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
